import React from 'react';

import Button from '../../../../library/button/button';
import Typography from '../../../../library/typography/typography';
import ReviewFeedback from '../../../reviews/components/review-feedback/review-feedback';
import FaceFrownRegularIcon from '../../../../shared/icons/face-frown-regular-icon';
import LoadingBlock from '../loading-block/loading-block';

import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import { ErrorData, Maybe } from '../../../../shared/types/graphql-types';

import './explanation.scss';

interface ExplanationProps {
  errors: Maybe<Array<ErrorData>>;
  explanation: Maybe<string>;
  fetchTreadwellExplanation: (forceFetch: boolean) => void;
  hideDisclaimer?: boolean;
  hideReviewFeedback?: boolean;
  id: string;
  isLoading: boolean;
  onHelpfulClick?: (id: string, isHelpful: boolean) => void;
  onProductComparison?: () => void;
  showErrorRefetchButton?: boolean;
  submittedFeedback?: { [id: string]: boolean };
}

function Explanation(props: ExplanationProps) {
  const {
    errors,
    explanation,
    fetchTreadwellExplanation,
    hideDisclaimer = false,
    hideReviewFeedback = false,
    id,
    isLoading,
    onHelpfulClick,
    onProductComparison,
    showErrorRefetchButton = false,
    submittedFeedback = {}
  } = props;

  if (isArrayWithLength(errors)) {
    return (
      <div styleName="error">
        <FaceFrownRegularIcon />
        <Typography weight="black">Oops, something went wrong!</Typography>
        {showErrorRefetchButton ? (
          <Button onClick={() => fetchTreadwellExplanation(true)}>
            Try again
          </Button>
        ) : (
          <Typography>Please check back later.</Typography>
        )}
      </div>
    );
  } else {
    return (
      <div styleName="container">
        {isLoading ? (
          <div data-testid="explanation__loading-container">
            <LoadingBlock />
            <div styleName="loading-considerations">
              <LoadingBlock />
              <LoadingBlock />
              <LoadingBlock />
            </div>
            <LoadingBlock styleName="loading-summary" />
          </div>
        ) : (
          <>
            {typeof explanation === 'string' && (
              <>
                <div
                  dangerouslySetInnerHTML={{ __html: explanation }}
                  styleName="markdown-container"
                />
                {typeof onProductComparison === 'function' && (
                  <Button
                    isSecondary
                    onClick={onProductComparison}
                    styleName="comparison-button"
                  >
                    See tire comparison
                  </Button>
                )}
              </>
            )}
            {!hideDisclaimer && (
              <div styleName="disclaimer">
                Generated with AI from professionally collected customer mileage
                and tire wear, blended with third party track testing for a
                complete evaluation of each tire.
              </div>
            )}
            {!hideReviewFeedback && (
              <ReviewFeedback
                id={id}
                isFeedbackSubmitted={Boolean(submittedFeedback[id])}
                isReportLabel
                key={id}
                onHelpfulClick={onHelpfulClick}
              />
            )}
          </>
        )}
      </div>
    );
  }
}

export default Explanation;
